import { useEffect, useMemo, useState } from 'react';
import { CheckCircle, Circle, Loader2, Trash2 } from 'lucide-react';
import { Preferences } from '@capacitor/preferences';
import api from '../../api/Client';
import { SecureStorage } from '../../storage/SecureStorage';
import { API_BASE } from '../../config';

// ====== Tipos ======
type CoachRaw = { id: number; name: string; profile_photo?: string | null; service?: string | null; centro?: string | null };
type PreferredCoachItem = { coach_id: number; priority?: number | null };
type PreferredCoachResponse = { preferred_coach_id?: number | null; preferred_coaches?: PreferredCoachItem[] };
export type StoredUser = {
  id: number;
  fullName: string;
  email: string;
  phone: string;
  sex: string;
  dateOfBirth: string;
  postcode?: number | null;
  postAddress?: string;
  dni?: string | null;
  profilePictureName?: string | null;
};
export type Professional = {
  id: number;
  name: string;
  photoName?: string | null;
  service?: string | null;
  centro?: string | null;
};

export default function FavoritesPage() {
  const [user, setUser] = useState<StoredUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [coaches, setCoaches] = useState<Professional[]>([]);
  const [favorites, setFavorites] = useState<PreferredCoachItem[]>([]);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [selectedCentro, setSelectedCentro] = useState<string>('guadarrama');

  // Helpers
  const coachImg = (photoName?: string | null) => {
    if (!photoName) return null;
    return `${API_BASE}/api/profile_pic/${photoName}`;
  };

  const normalizeFavorites = (items?: PreferredCoachItem[] | null) => {
    if (!Array.isArray(items)) return [];
    return items
      .filter(it => Number.isInteger(it.coach_id))
      .map(it => ({
        coach_id: Number(it.coach_id),
        priority: Number.isInteger(it.priority) ? Number(it.priority) : null,
      }))
      .sort((a, b) => (a.priority ?? 999) - (b.priority ?? 999))
      .slice(0, 3)
      .map((it, idx) => ({ ...it, priority: idx + 1 }));
  };

  const favoriteIds = useMemo(() => new Set(favorites.map(f => f.coach_id)), [favorites]);
  const nextPriority = favorites.length < 3 ? favorites.length + 1 : null;

  // Cargar usuario + lista + favoritos actuales
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        let currentUser: StoredUser | null = (await SecureStorage.getUser?.()) ?? null;
        if (!currentUser) {
          const { value } = await Preferences.get({ key: 'user' });
          if (value) currentUser = JSON.parse(value);
        }
        if (!currentUser) {
          setError('No se pudo cargar el usuario. Inicia sesion de nuevo.');
          setLoading(false);
          return;
        }
        setUser(currentUser);

        const list = await api.get<CoachRaw[]>('/api/mobile/list_coaches');
        const raw = Array.isArray(list.data) ? list.data : [];
        const mapped: Professional[] = raw
          .filter((r: any) => r.name !== 'Entrenador comodin')
          .map((r: any) => ({
            id: r.id,
            name: r.name,
            photoName: r.profile_photo ?? null,
            service: r.service ?? null,
            centro: r.centro ?? null,
          }));
        setCoaches(mapped);

        try {
          const pref = await api.get<PreferredCoachResponse>('/api/mobile/user/preferred-coach', {
            params: { customer_id: currentUser.id },
          });
          setFavorites(normalizeFavorites(pref.data?.preferred_coaches));
        } catch (e: any) {
          if (e?.response?.status === 404) {
            setFavorites([]);
          } else {
            setError(e?.message || 'Error cargando favoritos');
          }
        }
      } catch (e: any) {
        setError(e?.message || 'Error cargando favoritos');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const refreshFavorites = (items?: PreferredCoachItem[]) => {
    setFavorites(normalizeFavorites(items));
  };

  const onSelect = async (coach: Professional, priority?: number) => {
    if (!user) return;
    setBusyId(coach.id);
    try {
      const { data } = await api.post<PreferredCoachResponse>('/api/mobile/user/preferred-coach', {
        customer_id: user.id,
        coach_id: coach.id,
        priority,
      });
      refreshFavorites(data?.preferred_coaches);
    } catch (e: any) {
      setError(e?.message || 'No se pudo actualizar el favorito');
    } finally {
      setBusyId(null);
    }
  };

  const onRemove = async (coachId: number) => {
    if (!user) return;
    setBusyId(coachId);
    try {
      const { data } = await api.delete<PreferredCoachResponse>(
        `/api/mobile/user/preferred-coach/${coachId}?customer_id=${user.id}`
      );
      refreshFavorites(data?.preferred_coaches);
    } catch (e: any) {
      setError(e?.message || 'No se pudo eliminar el favorito');
    } finally {
      setBusyId(null);
    }
  };

  const trainers = useMemo(
    () =>
      coaches.filter(c => (c.service || '').toUpperCase().includes('ENTREN')).filter(c => {
        if (selectedCentro === 'todos') return true;
        if ((c.centro || '').toLowerCase() === 'vip') return true;
        return (c.centro || '').toLowerCase() === selectedCentro.toLowerCase();
      }),
    [coaches, selectedCentro]
  );

  return (
    <div style={{ position: 'relative', minHeight: '100vh', background: '#fff' }}>
      <div style={{ padding: 16 }}>
        {loading && <p style={{ marginTop: 12 }}>Cargando...</p>}
        {error && <p style={{ color: 'red', marginTop: 12 }}>{error}</p>}

        {!loading && !error && (
          <>
            <div style={{ marginBottom: 16 }}>
              <label style={{ marginRight: 8, fontWeight: 600 }}>Filtrar por centro:</label>
              <select
                value={selectedCentro}
                onChange={e => setSelectedCentro(e.target.value)}
                style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #ccc' }}
              >
                <option value="guadarrama">Guadarrama</option>
                <option value="sotillo">El Sotillo</option>
              </select>
            </div>

            <div style={{ marginBottom: 14 }}>
              <div style={{ fontWeight: 700, fontSize: 16 }}>Puedes elegir hasta 3 entrenadores favoritos:</div>
              <p style={{ marginTop: 6, color: '#444', fontSize: 13, lineHeight: '18px' }}>
                Tendremos en cuenta tus preferencias de entrenador, aunque la disponibilidad puede impedir atenderlas siempre. En cualquier caso,
                tu entrenador adaptara la sesion a tu historial y capacidades. Si algun ejercicio o carga no se ajusta a ti, por favor comunicalo.
              </p>
            </div>

            {/* Favoritos actuales */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <CheckCircle size={18} color="#10B981" />
                <div style={{ fontWeight: 700 }}>Tus favoritos (maximo 3)</div>
              </div>
              {favorites.length === 0 && <div style={{ color: '#666', fontSize: 13 }}>Aun no has elegido entrenadores favoritos.</div>}
              {favorites.length > 0 && (
                <div style={{ display: 'grid', gap: 8 }}>
                  {favorites.map((fav, index) => {
                    const coach = coaches.find(c => c.id === fav.coach_id);
                    if (!coach) return null;
                    const src = coachImg(coach.photoName);
                    const busy = busyId === coach.id;
                    return (
                      <div
                        key={coach.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 12,
                          padding: '12px 12px',
                          background: '#E0F7E9',
                          borderRadius: 12,
                          border: '1px solid #10B981',
                          opacity: busy ? 0.6 : 1,
                        }}
                      >
                        {src ? (
                          <img src={src} alt={coach.name} style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 24 }} />
                        ) : (
                          <div
                            style={{
                              width: 48,
                              height: 48,
                              borderRadius: 24,
                              background: '#e5e5e5',
                              display: 'grid',
                              placeItems: 'center',
                              fontSize: 11,
                              color: '#777',
                            }}
                          >
                            Sin foto
                          </div>
                        )}

                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 700 }}>{coach.name}</div>
                          <div style={{ color: '#0e9f6e', fontSize: 12 }}>Prioridad {index + 1}</div>
                        </div>

                        <select
                          disabled={busy}
                          value={index + 1}
                          onChange={e => onSelect(coach, Number(e.target.value))}
                          style={{ padding: '6px 8px', borderRadius: 8, border: '1px solid #ccc' }}
                        >
                          {[1, 2, 3].map(p => (
                            <option key={p} value={p} disabled={p > favorites.length}>
                              Prioridad {p}
                            </option>
                          ))}
                        </select>

                        <button
                          onClick={() => onRemove(coach.id)}
                          disabled={busy}
                          style={{
                            border: 'none',
                            background: 'transparent',
                            color: '#b91c1c',
                            cursor: busy ? 'not-allowed' : 'pointer',
                            padding: 6,
                          }}
                          title="Eliminar de favoritos"
                        >
                          {busy ? <Loader2 size={18} /> : <Trash2 size={18} />}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Lista de entrenadores (solo entrenamiento) */}
            <div style={{ marginBottom: 12, fontWeight: 700, fontSize: 16 }}>Entrenadores disponibles</div>
            {trainers.map(c => {
              const src = coachImg(c.photoName);
              const isFavorite = favoriteIds.has(c.id);
              const priority = favorites.find(f => f.coach_id === c.id)?.priority ?? null;
              const busy = busyId === c.id;
              const canAdd = !isFavorite && favorites.length < 3;

              return (
                <div
                  key={c.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '12px 12px',
                    background: isFavorite ? '#E0F7E9' : '#f7f7f7',
                    borderRadius: 12,
                    border: isFavorite ? '1px solid #10B981' : '1px solid transparent',
                    opacity: busy ? 0.6 : 1,
                    marginBottom: 10,
                  }}
                >
                  {src ? (
                    <img src={src} alt={c.name} style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 28 }} />
                  ) : (
                    <div
                      style={{
                        width: 56,
                        height: 56,
                        borderRadius: 28,
                        background: '#e5e5e5',
                        display: 'grid',
                        placeItems: 'center',
                        fontSize: 12,
                        color: '#777',
                      }}
                    >
                      Sin foto
                    </div>
                  )}

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700 }}>{c.name}</div>
                    {isFavorite ? (
                      <div style={{ color: '#0e9f6e', fontSize: 12 }}>Prioridad {priority}</div>
                    ) : (
                      <div style={{ color: '#666', fontSize: 12 }}>Pulsa para anadir como favorito</div>
                    )}
                  </div>

                  {busy ? (
                    <Loader2 size={18} />
                  ) : isFavorite ? (
                    <Circle size={20} color="#10B981" fill="#10B981" />
                  ) : (
                    <Circle size={20} color="#888" />
                  )}

                  <button
                    disabled={!canAdd || busy}
                    onClick={() => onSelect(c, nextPriority || undefined)}
                    style={{
                      marginLeft: 6,
                      padding: '8px 10px',
                      borderRadius: 8,
                      border: 'none',
                      background: canAdd ? '#B71C1C' : '#ccc',
                      color: '#fff',
                      fontWeight: 600,
                      cursor: canAdd && !busy ? 'pointer' : 'not-allowed',
                    }}
                  >
                    {canAdd ? `Anadir (P${nextPriority})` : 'Limite 3'}
                  </button>
                </div>
              );
            })}

            <p style={{ marginTop: 16, color: '#777', fontSize: 12 }}>
              Ordena tus favoritos con las prioridades de la lista superior. Pulsa en \"Anadir\" o mueve la prioridad desde la lista superior. Usa el icono de
              papelera para eliminarlos.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
